import type { CustomerSubscriptionDetail, SubscriptionUsage } from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  MoneyText,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CirclePause, CirclePlay, CircleX, ListChecks } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollTable, Th } from "../../../../features/customers/shared.js";
import { Section } from "../../../../features/fleet/shared.js";
import {
  entitlementPercent,
  formatEntitlement,
  subscriptionActions,
  subscriptionOwner,
} from "../../../../features/loyalty/loyalty-values.js";
import {
  useLoyaltyPermissions,
  useSubscription,
  useSubscriptionUsages,
} from "../../../../features/loyalty/queries.js";
import {
  LoyaltyPage,
  loyaltyCrumb,
  OwnerCell,
  Stat,
  SubscriptionStatusBadge,
} from "../../../../features/loyalty/shared.js";
import {
  type SubscriptionAction,
  SubscriptionActionDialog,
} from "../../../../features/loyalty/subscription-dialogs.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/sadakat/abonelikler/$id")({ component: SubscriptionRoute });

/**
 * Abonelik ayrıntısı (ADR 0017 §27): abone, faturalama biçimi, mevcut dönem ve bugünkü kalan hak (dönem / günlük), dönem
 * ücretleri, hak kullanımı defteri (iade karşı kaydı eksi, aşım işaretli — sunucu reddetmez) ve durum eylemleri
 * (duraklat / sürdür / iptal — iptal gerekçelidir).
 */
function SubscriptionRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = useLoyaltyPermissions();
  const subscription = useSubscription(id);
  const data = subscription.data;
  const [action, setAction] = useState<SubscriptionAction | null>(null);
  const actions = data ? subscriptionActions(data.status) : null;

  return (
    <LoyaltyPage
      title={data ? data.planName : t("loyalty.subscriptions.detailTitle")}
      breadcrumb={[
        loyaltyCrumb(
          t("menu.loyaltySubscriptions"),
          "/sadakat/abonelikler",
          data ? { company: data.companyId } : {},
        ),
        { label: data?.planCode ?? "…" },
      ]}
      actions={
        data && actions && permissions.canManageSubscriptions ? (
          <>
            {actions.canPause ? (
              <Button variant="outline" onClick={() => setAction("pause")}>
                <CirclePause aria-hidden="true" />
                {t("loyalty.subscriptions.actions.pause")}
              </Button>
            ) : null}
            {actions.canResume ? (
              <Button variant="outline" onClick={() => setAction("resume")}>
                <CirclePlay aria-hidden="true" />
                {t("loyalty.subscriptions.actions.resume")}
              </Button>
            ) : null}
            {actions.canCancel ? (
              <Button variant="outline" onClick={() => setAction("cancel")}>
                <CircleX aria-hidden="true" />
                {t("loyalty.subscriptions.actions.cancel")}
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      {subscription.error ? (
        <Alert tone="danger">{errorMessage(subscription.error)}</Alert>
      ) : !data ? (
        <SkeletonRows rows={8} />
      ) : (
        <>
          <SubscriptionSummary subscription={data} />
          <ChargesSection subscription={data} />
          <UsagesSection subscriptionId={data.id} />
          <SubscriptionActionDialog action={action} subscription={data} onClose={() => setAction(null)} />
        </>
      )}
    </LoyaltyPage>
  );
}

function SubscriptionSummary({ subscription }: { subscription: CustomerSubscriptionDetail }): ReactElement {
  const { t } = useTranslation();
  const usage = subscription.usage;
  const percent = entitlementPercent(usage.used, usage.quantityPerPeriod);
  return (
    <Section title={t("loyalty.subscriptions.summaryTitle")}>
      {subscription.cancelReason ? (
        <Alert tone="info">
          {t("loyalty.subscriptions.cancelReason", { reason: subscription.cancelReason })}
        </Alert>
      ) : null}
      <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={t("common.status")}>
          <SubscriptionStatusBadge status={subscription.status} />
        </Stat>
        <Stat label={t("loyalty.subscriptions.plan")} hint={subscription.planCode}>
          {subscription.planName}
        </Stat>
        <Stat label={t("loyalty.subscriptions.subscriber")}>
          <OwnerCell owner={subscriptionOwner(subscription)} showKind />
        </Stat>
        <Stat
          label={t("loyalty.subscriptions.billing")}
          hint={
            subscription.autoRenew
              ? t("loyalty.subscriptions.autoRenew")
              : t("loyalty.subscriptions.noAutoRenew")
          }
        >
          {subscription.billing === "INVOICE"
            ? t("loyalty.subscriptions.invoice")
            : t("loyalty.subscriptions.posBilling")}
        </Stat>
        <Stat label={t("loyalty.subscriptions.period")}>
          <span className="tabular">
            {formatDate(subscription.currentPeriodStart)} – {formatDate(subscription.currentPeriodEnd)}
          </span>
        </Stat>
        <div className="grid min-w-0 content-start gap-1">
          <dt className="text-xs text-muted-foreground">{t("loyalty.subscriptions.remaining")}</dt>
          {usage.periodStart === null ? (
            <dd className="text-sm text-muted-foreground">{t("loyalty.subscriptions.noCurrentPeriod")}</dd>
          ) : (
            <dd className="grid gap-1">
              <span className="text-sm font-medium tabular" data-testid="subscription-remaining">
                {t("loyalty.subscriptions.remainingText", {
                  remaining: formatEntitlement(usage.remaining),
                  total: formatEntitlement(usage.quantityPerPeriod),
                })}
              </span>
              {percent !== null ? (
                <div
                  role="progressbar"
                  aria-label={t("loyalty.subscriptions.usedLabel")}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                  className="h-2 w-full overflow-hidden rounded-full bg-muted"
                >
                  <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
                </div>
              ) : null}
            </dd>
          )}
        </div>
        <Stat label={t("loyalty.subscriptions.today")} hint={formatDate(usage.today)}>
          <span className="tabular">
            {usage.dailyLimit
              ? t("loyalty.subscriptions.todayText", {
                  used: formatEntitlement(usage.usedToday),
                  limit: formatEntitlement(usage.dailyLimit),
                })
              : t("loyalty.subscriptions.todayNoLimit", { used: formatEntitlement(usage.usedToday) })}
          </span>
        </Stat>
        <Stat label={t("loyalty.subscriptions.startedAt")}>
          <span className="tabular">{formatDateTime(subscription.startedAt)}</span>
        </Stat>
      </dl>
    </Section>
  );
}

function ChargesSection({ subscription }: { subscription: CustomerSubscriptionDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Section
      title={t("loyalty.subscriptions.chargesTitle")}
      description={t("loyalty.subscriptions.chargesHint")}
    >
      {subscription.charges.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {subscription.billing === "POS"
            ? t("loyalty.subscriptions.noChargesPos")
            : t("loyalty.subscriptions.noCharges")}
        </p>
      ) : (
        <ScrollTable label={t("loyalty.subscriptions.chargesTitle")}>
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr>
              <Th>{t("loyalty.subscriptions.period")}</Th>
              <Th>{t("loyalty.subscriptions.chargeSource")}</Th>
              <Th align="right">{t("loyalty.fields.amount")}</Th>
              <Th>{t("loyalty.subscriptions.chargedAt")}</Th>
            </tr>
          </thead>
          <tbody>
            {subscription.charges.map((charge) => (
              <tr key={charge.id} className="border-t">
                <td className="px-3 py-2 tabular whitespace-nowrap">
                  {formatDate(charge.periodStart)} – {formatDate(charge.periodEnd)}
                </td>
                <td className="px-3 py-2">
                  {charge.source === "INVOICE" ? (
                    <Link
                      to="/faturalar/$id"
                      params={{ id: charge.sourceId }}
                      className="underline underline-offset-4"
                    >
                      {t("loyalty.billing.openInvoice")}
                    </Link>
                  ) : (
                    <Link
                      to="/kasa/satislar/$id"
                      params={{ id: charge.sourceId }}
                      className="underline underline-offset-4"
                    >
                      {t("loyalty.reviews.openSale")}
                    </Link>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={charge.amount} />
                </td>
                <td className="px-3 py-2 tabular whitespace-nowrap">{formatDateTime(charge.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      )}
    </Section>
  );
}

function UsagesSection({ subscriptionId }: { subscriptionId: string }): ReactElement {
  const { t } = useTranslation();
  const pages = useCursorPages(subscriptionId);
  const usages = useSubscriptionUsages(subscriptionId, pages.cursor);
  const columns = useMemo<DataTableColumn<SubscriptionUsage>[]>(
    () => [
      {
        id: "occurredAt",
        header: t("loyalty.subscriptions.usedAt"),
        hideable: false,
        exportValue: (row) => row.occurredAt,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.occurredAt)}</span>,
      },
      {
        id: "quantity",
        header: t("loyalty.subscriptions.quantity"),
        align: "right",
        exportValue: (row) => row.quantity,
        cell: (row) => (
          <span className={`tabular ${row.quantity.startsWith("-") ? "text-destructive" : ""}`}>
            {formatEntitlement(row.quantity)}
          </span>
        ),
      },
      {
        id: "period",
        header: t("loyalty.subscriptions.periodStart"),
        exportValue: (row) => row.periodStart,
        cell: (row) => <span className="tabular">{formatDate(row.periodStart)}</span>,
      },
      {
        id: "sale",
        header: t("loyalty.reviews.sale"),
        exportValue: (row) => row.posSaleId ?? "",
        // Prompt 18: müşteri uygulamasından yıkama başlatmada kasa satışı yoktur (kaynak WASH).
        cell: (row) =>
          row.posSaleId ? (
            <Link
              to="/kasa/satislar/$id"
              params={{ id: row.posSaleId }}
              className="relative underline underline-offset-4"
            >
              {t("loyalty.reviews.openSale")}
              <span className="sr-only"> — {formatDateTime(row.occurredAt)}</span>
            </Link>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "overuse",
        header: t("loyalty.subscriptions.overuse"),
        exportValue: (row) => (row.overuse ? t("common.yes") : t("common.no")),
        cell: (row) =>
          row.overuse ? (
            <StatusBadge tone="warning">{t("loyalty.subscriptions.overuseYes")}</StatusBadge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    ],
    [t],
  );
  return (
    <Section title={t("loyalty.subscriptions.usagesTitle")}>
      <DataTable
        caption={t("loyalty.subscriptions.usagesTitle")}
        columns={columns}
        rows={usages.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={usages.isPending}
        error={usages.error ? <Alert tone="danger">{errorMessage(usages.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={3}
            icon={<ListChecks />}
            title={t("loyalty.subscriptions.usagesEmptyTitle")}
            description={t("loyalty.subscriptions.usagesEmptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: usages.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(usages.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
    </Section>
  );
}
